import { EarningsItemBase, Entity } from '@api';
import { HighLowChart } from '@design/charts/high-low-chart/HighLowChart';
import { Button } from '@design/components/button/Button';
import { formatPriceWithCurrency } from '@helpers';
import { WebOnlyFeat } from '@terminal/config/WebOnlyFeat';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { TabsType } from '@terminal/modules/tab-content/types';
import { ScenariosData } from '@terminal/react-query-hooks/scenarios/useScenarios';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { ChartLegend } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/earnings-event-drawer-content/earnings-chart/chart-legend/ChartLegend';
import { Expectation } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/earnings-event-drawer-content/types';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './HistoricalReaction.module.scss';

const CHART_HEIGHT = 300;

interface HistoricalReactionProps {
  entity: Entity;
  earningsData: EarningsItemBase;
  scenariosData: ScenariosData[];
  width: number;
}

export const HistoricalReaction = ({
  entity,
  earningsData,
  scenariosData,
  width,
}: HistoricalReactionProps) => {
  const { t } = useTranslation(['earnings']);
  const { navigateToPage } = useNavigateToAssetOverview();

  const epsSurprise = earningsData?.eps.surprise;
  const expectation =
    epsSurprise !== undefined
      ? epsSurprise >= 0
        ? Expectation.Beat
        : Expectation.Miss
      : undefined;

  const entityName = entity.name_short || entity.name;

  const searchParams = useMemo(() => {
    const params = new URLSearchParams();

    if (earningsData?.eps?.surprise !== undefined && expectation) {
      params.append(
        queryKeys.epsFilter,
        expectation === Expectation.Beat ? 'beats' : 'misses'
      );
      params.append(queryKeys.epsValue, earningsData.eps.surprise.toString());
    }

    return params;
  }, [earningsData, expectation]);

  const handleDigIntoData = useCallback(() => {
    navigateToPage({
      entityTag: entity.tag,
      source: POSTHOG_TRACKING_SOURCES.EARNINGS_INSIGHT_DRAWER,
      tab: TabsType.Earnings,
      searchParams,
    });
  }, [navigateToPage, entity.tag, searchParams]);

  const mappedScenariosData = useMemo(() => {
    if (!scenariosData?.length) {
      return [];
    }

    const firstScenario = scenariosData[0];

    return (
      firstScenario?.predictions?.map(item => ({
        high: item.high,
        median: item.median,
        low: item.low,
        label: t(item.horizon),
      })) ?? []
    );
  }, [scenariosData, t]);

  const valueFormatted = formatPriceWithCurrency(
    earningsData.eps.surprise ?? 0,
    earningsData.currency
  );

  return (
    <div className={styles.root}>
      <div className={styles.flexRow}>
        <div className={styles.chartTitleLabel}>
          {expectation === Expectation.Beat
            ? t(
                'earnings:earningsInsight.drawer.earningsRecap.stockPerformanceAfterEarningsBeats',
                {
                  name: entityName,
                  count: scenariosData[0]?.episodes?.length ?? 0,
                  value: valueFormatted,
                }
              )
            : t(
                'earnings:earningsInsight.drawer.earningsRecap.stockPerformanceAfterEarningsMisses',
                {
                  name: entityName,
                  count: scenariosData[0]?.episodes?.length ?? 0,
                  value: valueFormatted,
                }
              )}
        </div>
        <WebOnlyFeat>
          <Button
            className={styles.styledButton}
            label={t(
              'earnings:earningsInsight.drawer.earningsPreview.digIntoTheData'
            )}
            variant="empty"
            onClick={handleDigIntoData}
          />
        </WebOnlyFeat>
      </div>
      <ChartLegend />
      <div data-testid="chart-container">
        <HighLowChart
          data={mappedScenariosData}
          width={width}
          height={CHART_HEIGHT}
          variant="secondary"
          showAreaFill={false}
        />
      </div>
    </div>
  );
};
